/**
 * The Empress (docs/spec/04 §1, 03 §5): 64 x 40, built from shapes rather than drawn by hand so
 * her two halves always match. A crowned head, a thorax with a bright core, four wings.
 */

import { CYAN, GOLD, INDIGO, MAGENTA, PINK, PURPLE, RED, WHITE, YELLOW } from '../palette';
import type { SpriteDef } from './types';
import { mirror, recolour } from './types';

const HALF_W = 32;
const H = 40;

/** Inside an ellipse centred (cx, cy) with radii (rx, ry): 0 at the centre, 1 on the edge. */
function ellipse(x: number, y: number, cx: number, cy: number, rx: number, ry: number): number {
  const dx = (x - cx) / rx;
  const dy = (y - cy) / ry;
  return Math.sqrt(dx * dx + dy * dy);
}

/** One pixel of her left half; x runs 0 (wing tip) to 31 (the spine). `flex` lifts the wings. */
function pixel(x: number, y: number, flex: number): string {
  const px = x + 0.5;
  const py = y + 0.5;
  // The core, eyes and crown sit over everything.
  if (ellipse(px, py, 32, 21, 3.2, 4.2) <= 1) return ellipse(px, py, 32, 21, 1.8, 2.6) <= 1 ? 'W' : 'C';
  if (ellipse(px, py, 27.5, 10.5, 2.4, 1.7) <= 1) return 'E';
  for (const [sx, tip, base] of [[23, 0, 7], [27, 2, 7], [31, 1, 7]] as const) {
    if (py >= tip && py <= base && Math.abs(px - sx) <= ((py - tip) / (base - tip)) * 1.8 + 0.4) return 'G';
  }
  const head = ellipse(px, py, 32, 11, 9.5, 5.6);
  if (head <= 1) return head > 0.8 ? 'D' : py < 9 ? 'L' : 'M';
  const body = ellipse(px, py, 32, 23, 7.5, 13.5);
  if (body <= 1) {
    if (body > 0.84) return 'D';
    // Segments down the abdomen.
    return y > 26 && y % 3 === 0 ? 'D' : px < 28 ? 'M' : 'L';
  }
  if (py > 34 && Math.abs(px - 32) <= (40 - py) * 0.5) return 'G';
  const upper = ellipse(px, py + flex * (30 - px) * 0.12, 13, 15, 14.5, 8.5);
  const lower = ellipse(px, py - flex * (30 - px) * 0.08, 16, 28, 11, 6.2);
  const wing = Math.min(upper, lower);
  if (wing <= 1) {
    if (wing > 0.86) return 'P';
    // Veins fan out from the shoulder.
    const vein = Math.floor(Math.atan2(py - 19, 30 - px) * 7 + 20);
    if (vein % 3 === 0) return 'P';
    return wing < 0.45 ? 'K' : 'V';
  }
  return '.';
}

function half(flex: number): string[] {
  const rows: string[] = [];
  for (let y = 0; y < H; y++) {
    let row = '';
    for (let x = 0; x < HALF_W; x++) row += pixel(x, y, flex);
    rows.push(row);
  }
  return rows;
}

export const EMPRESS: SpriteDef = {
  frames: [mirror(half(0)), mirror(half(1))],
  colours: {
    M: MAGENTA, L: PINK, D: INDIGO, P: PURPLE, V: '#5a2a9c', K: '#7a3fc4', G: GOLD, E: CYAN, C: YELLOW, W: WHITE,
  },
};

/** Phase two: her eyes and core burn red. */
export const EMPRESS_ANGRY: SpriteDef = recolour(EMPRESS, { E: RED, C: RED, G: YELLOW });
